import React, { useEffect, useState } from 'react';
import propTypes from 'prop-types';
import Select from 'react-select';
import { Alert, Button, Col, FormGroup, Label, Row } from 'reactstrap';
import { apiClient } from '../../utils/Api';
import SelectVariant from '../../components/Form/Select/Async/SelectVariant';
import SelectSizeAsync from '../Components/SelectSizeAsync/SelectSizeAsync';
import Defects from '../SellRequests/Components/RequestForm/Components/Defects/Defects';
import { BOX_CONDITION_OPTIONS, LISTING_CONDITION_OPTIONS } from '../SellRequests/Components/RequestForm/RequestForm.data';
import { emptyQcFlags, initialReturnQcForm } from './returnQcForm';
import { returnErrorMessage } from './returnSale';

const withCurrentOption = (options, current) => (
  !current || options.some((option) => option.value === current) ? options : [...options, { value: current, label: current }]
);

const ReturnQcForm = ({ saleId, value, onChange, disabled }) => {
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    onChange(null);
    apiClient().get(`/admins/sales/${saleId}`).then(({ data }) => {
      if (cancelled) return;
      onChange(initialReturnQcForm(data.data));
    }).catch((err) => {
      if (!cancelled) setError(returnErrorMessage(err));
    });
    return () => { cancelled = true; };
  }, [saleId, attempt, onChange]);

  if (error) return (
    <Alert color="danger">
      {error}
      <Button type="button" size="sm" color="secondary" className="ml-2" onClick={() => setAttempt((count) => count + 1)}>Retry</Button>
    </Alert>
  );
  if (!value) return <p>Loading listing details...</p>;

  return <ReturnListingFields value={value} onChange={onChange} disabled={disabled} />;
};

export const ReturnListingFields = ({ value, onChange, disabled, idPrefix = 'return-qc', productOnly = false, conditionOnly = false }) => {

  const changeFlag = (event) => {
    const { name, checked } = event.target;
    const [group, key] = name.split('.');
    onChange((current) => ({ ...current, [group]: { ...current[group], [key]: checked } }));
  };
  const resetAccessories = () => {
    onChange((current) => ({ ...current, missing_accessories_list: emptyQcFlags().missing_accessories_list }));
  };

  return (
    <fieldset disabled={disabled}>
      {!conditionOnly ? <Row>
      <Col xs={12} md={6}>
      <FormGroup>
        <Label for={`${idPrefix}-product`}>Product Variant*</Label>
        <SelectVariant
          id={`${idPrefix}-product`}
          labelWithSku
          value={value.product}
          disabled={disabled}
          onSelectChange={(product) => onChange((current) => ({
            ...current,
            ...(product && current.product && product.category.toLowerCase() === current.product.category ? {} : emptyQcFlags()),
            product: product ? { ...product, category: product.category.toLowerCase() } : null,
            size: product && current.product && product.id === current.product.id ? current.size : null,
          }))}
        />
      </FormGroup>
      </Col>
      <Col xs={12} md={6}>
      <FormGroup>
        <Label for={`${idPrefix}-size`}>Size*</Label>
        <SelectSizeAsync
          id={`${idPrefix}-size`}
          value={value.size}
          productId={value.product && value.product.id}
          disabled={disabled || !value.product}
          onChange={(size) => onChange((current) => ({ ...current, size }))}
        />
      </FormGroup>
      </Col>
      </Row> : null}
      {!productOnly ? <>
      <Row>
      <Col xs={12} md={6}>
      <FormGroup>
        <Label for={`${idPrefix}-condition`}>Listing Condition*</Label>
        <Select
          id={`${idPrefix}-condition`}
          options={withCurrentOption(LISTING_CONDITION_OPTIONS, value.sneakers_condition)}
          value={value.sneakers_condition}
          disabled={disabled}
          clearable={false}
          onChange={(option) => onChange((current) => ({ ...current, sneakers_condition: option ? option.value : '' }))}
        />
      </FormGroup>
      </Col>
      <Col xs={12} md={6}>
      <FormGroup>
        <Label for={`${idPrefix}-box`}>Box Condition*</Label>
        <Select
          id={`${idPrefix}-box`}
          options={withCurrentOption(BOX_CONDITION_OPTIONS, value.box_condition)}
          value={value.box_condition}
          disabled={disabled}
          clearable={false}
          onChange={(option) => onChange((current) => ({ ...current, box_condition: option ? option.value : '' }))}
        />
      </FormGroup>
      </Col>
      </Row>
      <Defects formValue={value} onChangeValue={changeFlag} resetDefectDetail={resetAccessories} />
      </> : null}
    </fieldset>
  );
};

ReturnQcForm.propTypes = {
  saleId: propTypes.number.isRequired,
  value: propTypes.object,
  onChange: propTypes.func.isRequired,
  disabled: propTypes.bool,
};

ReturnListingFields.propTypes = {
  value: propTypes.object.isRequired,
  onChange: propTypes.func.isRequired,
  disabled: propTypes.bool,
  idPrefix: propTypes.string,
  productOnly: propTypes.bool,
  conditionOnly: propTypes.bool,
};

export default ReturnQcForm;
